import { Tooltip as ReactTooltip } from "react-tooltip";
import { useEffect, useRef, useState } from "react";
import styles from "./GlossarTooltip.module.scss";
import { ImageType } from "@/types/baseTypes";
import { MediaItem } from "../Global/MediaItem/MediaItem";

const GlossarTooltip = ({}) => {
  const [tooltipData, setTooltipData] = useState<{
    title: string;
    excerpt: string;
    link: string;
    image: ImageType | false;
  }>({
    title: "",
    excerpt: "",
    link: "",
    image: false,
  });
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const isHovering = useRef<boolean>(false);

  useEffect(() => {
    const linkList: HTMLAnchorElement[] = [];

    const handleLinkHover = async (event: Event) => {
      event.preventDefault();
      const { target } = event;
      isHovering.current = true;
      setShowTooltip(true);

      if (
        target instanceof HTMLAnchorElement &&
        target.classList.contains("glossar-link")
      ) {
        // Fetch data for the encyclopedia custom post type
        try {
          const data = target.getAttribute("data-content");
          const content = data ? JSON.parse(data as string) : undefined;
          if (content && isHovering.current) {
            setTooltipData(content);
            return;
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };
    const handleLinkLeave = (event: Event) => {
      event.preventDefault();
      isHovering.current = false;
      setShowTooltip(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "t":
          handleLinkHover(event);
          break;
        case "Escape":
          handleLinkLeave(event);
          break;
        default:
          break;
      }
    };

    const detachListeners = () => {
      linkList.forEach((link: HTMLAnchorElement) => {
        link.removeEventListener("mouseenter", handleLinkHover);
        link.removeEventListener("mouseleave", handleLinkLeave);
        link.removeEventListener("keydown", onKeyDown);
        link.removeEventListener("blur", handleLinkLeave);
      });
    };

    // Create an observer instance
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const links =
              node.querySelectorAll<HTMLAnchorElement>("a.glossar-link");
            links.forEach((link) => {
              if (!linkList.includes(link)) {
                link.addEventListener("mouseover", handleLinkHover);
                link.addEventListener("mouseout", handleLinkLeave);
                link.addEventListener("keydown", onKeyDown);
                link.addEventListener("blur", handleLinkLeave);
                linkList.push(link);
              }
            });
          }
        });
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial setup for existing links
    document
      .querySelectorAll<HTMLAnchorElement>("a.glossar-link")
      .forEach((link) => {
        link.addEventListener("mouseenter", handleLinkHover);
        link.addEventListener("mouseleave", handleLinkLeave);
        link.addEventListener("keydown", onKeyDown);
        link.addEventListener("blur", handleLinkLeave);
        linkList.push(link);
      });

    //Glossar add EventsListeners

    return () => {
      observer.disconnect();
      detachListeners();
    };
  }, []);

  return (
    <ReactTooltip
      clickable
      id="glossar-tooltip"
      anchorSelect={".glossar-link"}
      place="bottom"
      className={`${styles["glossar-tooltip"]} ${tooltipData.image ? styles["has-image"] : ""}`}
      isOpen={showTooltip}
      afterHide={() => {
        setTooltipData({
          title: "",
          excerpt: "",
          link: "",
          image: false,
        });
      }}
    >
      {(tooltipData.title === "" || tooltipData.excerpt === "") && (
        <div className="loader" />
      )}
      {tooltipData.image ? (
        <>
          <MediaItem
            showModal={false}
            sizes={"(max-width: 768px) 30vw, 20vw"}
            image_desktop={tooltipData.image}
          />
        </>
      ) : (
        <>
          {tooltipData.excerpt && (
            <span
              className={`${styles["tooltip-content"]}`}
              dangerouslySetInnerHTML={{ __html: tooltipData.excerpt }}
            />
          )}
        </>
      )}
    </ReactTooltip>
  );
};

export default GlossarTooltip;
